import $http from '@vue-util/http';
import { UserStatus } from '@vue-util/cookie';
import logger from '@vue-util/logger';
import { UOP_RETURN_DETAILS_ENDPOINT, RETURNS_BASE_PATH } from '#shared/endpoints';
import { createHeaders } from '@/utils/CreateHeaders';
import { RETURN_DETAILS_ERROR } from 'config';
import { getBaseUrl } from '@/utils/General';

export const useReturnDetailStore = defineStore('returnDetailStore', {
  state: () => ({
    returnDetails: {},
    error: '',
    isLoading: true,
    returnLabel: {
      showNotification: false,
      emailAddress: '',
      error: false,
    },
  }),
  getters: {
    SSRE_RDV: (state) => state.returnDetails.substituteShipments?.[0]?.type || false,
  },
  actions: {
    SET_RETURN_DETAILS(returnDetails) {
      this.returnDetails = returnDetails;
    },
    SET_RETURN_DETAILS_ERROR(val) {
      this.error = val;
    },
    SET_RETURN_DETAILS_LOADER(isRdvLoading) {
      this.isLoading = isRdvLoading;
    },
    SET_SHOW_RETURNLABEL_NOTIFICATION(payload) {
      this.returnLabel.emailAddress = payload.emailAddress;
      this.returnLabel.showNotification = payload.showReturnLabelNotification;
    },
    SET_RETURNLABEL_ERROR(error) {
      this.returnLabel.error = error;
    },
    async GET_RETURN_DETAILS({ pendingReturnId, token, rdv, orderNumber, ssrContext = {} }) {
      this.SET_RETURN_DETAILS_LOADER(true);
      this.SET_RETURN_DETAILS_ERROR(false);
      try {
        const { cookie, 'user-agent': userAgent } = ssrContext;
        const headersOptions = createHeaders({ ssrCookies: cookie, userAgent });
        const options = {
          ...headersOptions,
          params: {},
        };
        if (pendingReturnId && orderNumber) {
          options.params.pidToken = pendingReturnId;
          options.params.partnerPurchaseId = orderNumber;
        } else if (token) options.params.token = token;
        if (rdv) options.params.rdv = rdv;

        const isSignedIn = import.meta.server ? !!cookie?.match(/SignedIn=1/) : UserStatus.isSignedIn();
        options.params.verifyLookup = !isSignedIn ? 'true' : 'false';
        const baseUrl = getBaseUrl();
        const response = await $http.get(`${baseUrl}${UOP_RETURN_DETAILS_ENDPOINT}`, options);

        if (import.meta.server) {
          const { findAndDecode } = await import('@/utils/DecodeResponse');
          response.data = findAndDecode(response.data, ['orderToken', 'shipmentToken', 'pendingReturnId', 'pidToken']);
        }

        this.SET_RETURN_DETAILS(response.data.returnDetails);
        return null;
      } catch (error) {
        const { errorCode = '', message = '' } = error?.response?.data?.errors?.error?.[0] ?? {};
        this.SET_RETURN_DETAILS_ERROR(RETURN_DETAILS_ERROR);
        logger.info(
          `[PPE] GET_RETURN_DETAILS: ERROR caught: ${JSON.stringify({
            status: error?.status,
            data: error?.response?.data,
            message: error?.message,
          })}`,
        );
        return {
          errorCode,
          message: RETURN_DETAILS_ERROR,
          miscMessage: message,
          errorLabel: 'return details',
        };
      } finally {
        this.SET_RETURN_DETAILS_LOADER(false);
      }
    },
    async SEND_RETURN_LABEL_EMAIL({ labelUrl, emailAddress }) {
      try {
        const url = `${RETURNS_BASE_PATH}/resend-label`;
        const payload = { returnDetails: this.returnDetails };
        const headersOptions = createHeaders();
        const options = {
          ...headersOptions,
          params: {
            verifyLookup: 'true',
            lookupParams: decodeURIComponent(labelUrl),
            resendEmailId: emailAddress,
          },
        };

        const response = await $http.post(url, payload, options);
        if (response.status === 200) {
          this.SET_SHOW_RETURNLABEL_NOTIFICATION({
            emailAddress,
            showReturnLabelNotification: true,
          });
        }
      } catch (error) {
        this.SET_RETURNLABEL_ERROR(true);
        throw error;
      }
    },
    CLEAR_RETURN_DETAILS() {
      this.SET_RETURN_DETAILS({});
      this.SET_RETURN_DETAILS_ERROR(false);
      this.SET_SHOW_RETURNLABEL_NOTIFICATION({
        emailAddress: '',
        showReturnLabelNotification: false,
      });
      this.SET_RETURNLABEL_ERROR(false);
    },
  },
});
